import { SectorModel } from 'src/app/models/form-common/sector.model';
import { FertilizantesEnvironmentDefinition } from '../../environment-variables';
import { FertilizantesListsDefinition } from './list-variables';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { Utils } from 'src/app/common/utils';
import { TareaFertiModel } from 'src/app/models/tareas/tarea-ferti.model';
import type {
	DropdownData,
	FilteredValue,
} from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import { ReactiveFormValue } from 'src/app/common/components/reactive-form-builder/interfaces/reactive-form-change.interface';
import { getDropdownSelectedArray } from 'src/app/common/components/reactive-form-builder/utils/dropdown-data.utils';

type NpkRowVm = {
	name: string;
	superficieLabel: string;
	n: string;
	p: string;
	k: string;
	ca?: string;
	s?: string;
	mg?: string;
	hasSurface: boolean;
};

type NpkTotalsVm = {
	superficie: string;
	n: string;
	p: string;
	k: string;
	ca?: string;
	s?: string;
	mg?: string;
};

export class BuildNPKTable {
	public env: FertilizantesEnvironmentDefinition = new FertilizantesEnvironmentDefinition();
	public lists: FertilizantesListsDefinition = new FertilizantesListsDefinition();

	public model: ReactiveFormValue<TareaFertiModel> = {} as ReactiveFormValue<TareaFertiModel>;

	public sectorRowsVm: NpkRowVm[] = [];
	public parcelaRowsVm: NpkRowVm[] = [];

	public totalsVm: NpkTotalsVm = { superficie: '0,00', n: '0,00', p: '0,00', k: '0,00' };
	public parcelaTotalsVm: NpkTotalsVm = { superficie: '0,00', n: '0,00', p: '0,00', k: '0,00' };

	public superfSect: number[] = [];

	public udFertN: number[] = [];
	public udFertP: number[] = [];
	public udFertK: number[] = [];
	public udFertCa: number[] = [];
	public udFertS: number[] = [];
	public udFertMg: number[] = [];

	public totalN = 0;
	public totalP = 0;
	public totalK = 0;
	public totalCa = 0;
	public totalS = 0;
	public totalMg = 0;

	public totalesTabla = {
		superficie: 0,
		n: 0,
		p: 0,
		k: 0,
		ca: 0,
		mg: 0,
		s: 0,
	};

	private readonly zeroLabel = '0,00';

	// -------------- PUBLIC --------------

	/**
	 * Calculates NPK and related nutrients and builds table VMs.
	 * Reads products/dosis from semicolon fields: model.id_producto, model.dosis
	 */
	public calculaNPKs(): void {
		const appName = this.env.appName;
		const isProsanzcu = appName === 'prosanzcu';
		const isSequiador = appName === 'sequiador';
		const isEsparragosGranada = appName === 'esparragosgranada';
		const isAbonosFolquesOrPyf = appName === 'abonosfolques' || appName === 'pyf';

		const showSectorMultiple = !!this.env.showTareasFertiSectorMultiple;
		const udsByParcela = !!this.env.udsByParcela;

		const superfTotal = this.getTotalSuperficieParcelas();

		// Reset totals
		this.totalN = 0;
		this.totalP = 0;
		this.totalK = 0;
		this.totalCa = 0;
		this.totalS = 0;
		this.totalMg = 0;

		// 1) Products totals (serialized id_producto + dosis)
		this.sumProductsTotalsFromSerializedFields(isProsanzcu, superfTotal);

		// 2) Sequiador adds
		const totalNSequiador = isSequiador ? this.totalN + this.toNum(this.model.n_agua) : 0;
		const totalPSequiador = isSequiador ? this.totalP + this.toNum(this.model.p205) : 0;

		// 3) Selected parcelas set
		const idsParcelasSet = this.buildSelectedParcelasSet(this.model.id_parcelas);

		// 4) Build superfSect (sectorMultiple uses parcelas.filtered, else sector surfaces)
		this.buildSuperfSect(idsParcelasSet, showSectorMultiple);

		// Reset totals table
		this.resetTotalesTabla();

		// Reset arrays (reuse same instances)
		this.udFertN.length = 0;
		this.udFertP.length = 0;
		this.udFertK.length = 0;
		this.udFertCa.length = 0;
		this.udFertS.length = 0;
		this.udFertMg.length = 0;

		// 5) udsByParcela
		if (udsByParcela) {
			const parcelasSelected: ParcelasModel[] = getDropdownSelectedArray(this.lists.parcelas);

			for (let i = 0; i < parcelasSelected.length; i++) {
				const parcela = parcelasSelected[i];
				const parcelaIdStr = String(parcela?.id ?? '');

				if (!idsParcelasSet.has(parcelaIdStr)) {
					// Leave undefined -> VM prints "-"
					continue;
				}

				const area = this.toNum(parcela?.superficie_cultivada ?? parcela?.superficie_sigpac ?? '0.00');

				if (!this.isValidArea(area)) {
					this.udFertN[i] = 0;
					this.udFertP[i] = 0;
					this.udFertK[i] = 0;
					this.udFertCa[i] = 0;
					this.udFertS[i] = 0;
					this.udFertMg[i] = 0;
					continue;
				}

				// Per-parcela values shown in the parcelas table
				if (isSequiador) {
					// NOTE: legacy behavior: N/P are totals, K/Ca/S/Mg are per ha
					this.udFertN[i] = totalNSequiador;
					this.udFertP[i] = totalPSequiador;
					this.udFertK[i] = this.totalK / area;
					this.udFertCa[i] = this.totalCa / area;
					this.udFertS[i] = this.totalS / area;
					this.udFertMg[i] = this.totalMg / area;
				} else if (isProsanzcu) {
					// Preserve legacy behavior
					this.udFertN[i] = this.totalN * area;
					this.udFertP[i] = this.totalP * area;
					this.udFertK[i] = this.totalK * area;
					this.udFertCa[i] = this.totalCa * area;
					this.udFertS[i] = this.totalS * area;
					this.udFertMg[i] = this.totalMg * area;
				} else {
					// Normal: per ha
					this.udFertN[i] = this.totalN / area;
					this.udFertP[i] = this.totalP / area;
					this.udFertK[i] = this.totalK / area;
					this.udFertCa[i] = this.totalCa / area;
					this.udFertS[i] = this.totalS / area;
					this.udFertMg[i] = this.totalMg / area;
				}

				// Surface accumulation is still useful
				this.totalesTabla.superficie += area;

				// IMPORTANT FIX:
				// Do NOT sum per-parcela (kg/ha) values into header totals.
				// Header table must reflect product totals like the legacy app.
			}

			// Header totals must be product totals (legacy behavior)
			this.totalesTabla.n = isSequiador ? totalNSequiador : this.totalN;
			this.totalesTabla.p = isSequiador ? totalPSequiador : this.totalP;
			this.totalesTabla.k = this.totalK;
			this.totalesTabla.ca = this.totalCa;
			this.totalesTabla.s = this.totalS;
			this.totalesTabla.mg = this.totalMg;

			this.buildNpkVms();
			return;
		}

		// 6) udsBySector (uses superfSect indexes)
		for (let i = 0; i < (this.superfSect?.length ?? 0); i++) {
			const s = this.toNum(this.superfSect[i]);

			if (!this.isValidArea(s)) {
				this.udFertN[i] = 0;
				this.udFertP[i] = 0;
				this.udFertK[i] = 0;
				this.udFertCa[i] = 0;
				this.udFertS[i] = 0;
				this.udFertMg[i] = 0;
				continue;
			}

			if (isEsparragosGranada || isProsanzcu) {
				this.udFertN[i] = this.totalN * s;
				this.udFertP[i] = this.totalP * s;
				this.udFertK[i] = this.totalK * s;
				this.udFertCa[i] = this.totalCa * s;
				this.udFertS[i] = this.totalS * s;
				this.udFertMg[i] = this.totalMg * s;
			} else {
				this.udFertN[i] = this.totalN / s;
				this.udFertP[i] = this.totalP / s;
				this.udFertK[i] = this.totalK / s;
				this.udFertCa[i] = this.totalCa / s;
				this.udFertS[i] = this.totalS / s;
				this.udFertMg[i] = this.totalMg / s;
			}

			this.totalesTabla.superficie += s;
			this.totalesTabla.n += this.udFertN[i] ?? 0;
			this.totalesTabla.p += this.udFertP[i] ?? 0;
			this.totalesTabla.k += this.udFertK[i] ?? 0;
			this.totalesTabla.ca += this.udFertCa[i] ?? 0;
			this.totalesTabla.s += this.udFertS[i] ?? 0;
			this.totalesTabla.mg += this.udFertMg[i] ?? 0;
		}

		// 7) Special distribution (abonosfolques / pyf)
		if (isAbonosFolquesOrPyf) {
			const totalSuperficie = this.toNum(this.totalesTabla.superficie);

			this.totalesTabla.n = 0;
			this.totalesTabla.p = 0;
			this.totalesTabla.k = 0;
			this.totalesTabla.ca = 0;

			for (let i = 0; i < (this.superfSect?.length ?? 0); i++) {
				const superf = this.toNum(this.superfSect[i]);

				if (!this.isValidArea(totalSuperficie) || !Number.isFinite(superf) || superf < 0) {
					this.udFertN[i] = 0;
					this.udFertP[i] = 0;
					this.udFertK[i] = 0;
					this.udFertCa[i] = 0;
				} else {
					const ratio = superf / totalSuperficie;
					this.udFertN[i] = ratio * this.totalN;
					this.udFertP[i] = ratio * this.totalP;
					this.udFertK[i] = ratio * this.totalK;
					this.udFertCa[i] = ratio * this.totalCa;
				}

				this.totalesTabla.n += this.udFertN[i] ?? 0;
				this.totalesTabla.p += this.udFertP[i] ?? 0;
				this.totalesTabla.k += this.udFertK[i] ?? 0;
				this.totalesTabla.ca += this.udFertCa[i] ?? 0;
			}
		}

		this.buildNpkVms();
	}

	/**
	 * Optimized surface totals calculation (single-pass).
	 */
	public getTotalSuperficieParcelas(): number {
		let sum = 0;
		let selected: SectorModel[] | ParcelasModel[] = [];

		if (this.env.showFertilizantesParcelas) {
			selected = getDropdownSelectedArray(this.lists.parcelas);

			for (const parcela of selected) {
				const raw = parcela.superficie_cultivada ?? parcela.superficie_sigpac;

				if (!raw) {
					continue;
				}

				sum += this.toNum(raw);
			}

			return sum;
		}

		selected = getDropdownSelectedArray(this.lists.sectores);

		for (const sector of selected) {
			const raw = sector.superficie_cultivada ?? sector.superficie_sigpac;

			if (!raw) {
				continue;
			}

			sum += this.toNum(raw);
		}

		return sum;
	}

	// -------------- PRIVATE: PRODUCTS FROM SERIALIZED FIELDS --------------

	private sumProductsTotalsFromSerializedFields(isProsanzcu: boolean, superfTotal: number): void {
		const ids = this.parseSemicolon(String(this.model.ids_productos ?? ''));
		const dosis = this.parseSemicolon(String(this.model.dosis ?? ''));

		if (ids.length === 0) return;

		const productById = this.buildProductosMap();
		const len = Math.max(ids.length, dosis.length);

		for (let i = 0; i < len; i++) {
			const idStr = (ids[i] ?? '').trim();
			if (!idStr) continue;

			const idNum = Number(idStr);
			const producto = productById.get(Number.isFinite(idNum) ? idNum : idStr);
			if (!producto) continue;

			const d = this.toNum(dosis[i] ?? 0);

			let n = (d * this.toNum((producto as any).N)) / 100;
			let p = (d * this.toNum((producto as any).P)) / 100;
			let k = (d * this.toNum((producto as any).K)) / 100;

			const ca = (d * this.toNum((producto as any).Ca)) / 100;
			const s = (d * this.toNum((producto as any).S)) / 100;
			const mg = (d * this.toNum((producto as any).Mg)) / 100;

			if (isProsanzcu && this.isValidArea(superfTotal)) {
				n /= superfTotal;
				p /= superfTotal;
				k /= superfTotal;
			}

			this.totalN += n;
			this.totalP += p;
			this.totalK += k;
			this.totalCa += ca;
			this.totalS += s;
			this.totalMg += mg;
		}
	}

	private buildProductosMap(): Map<unknown, ProductosModel> {
		const map = new Map<unknown, ProductosModel>();

		const dd = this.lists.productos as unknown as DropdownData<ProductosModel> | undefined;
		if (!dd) return map;

		const filtered = (dd.filtered ?? []) as Array<FilteredValue<ProductosModel>>;
		for (let i = 0; i < filtered.length; i++) {
			const p = filtered[i]?.value;
			if (!p) continue;
			map.set(p.id, p);
			map.set(String(p.id), p);
		}

		const values = dd.values ?? [];
		for (let i = 0; i < values.length; i++) {
			const p = values[i]?.value;
			if (!p) continue;
			if (!map.has(p.id)) {
				map.set(p.id, p);
				map.set(String(p.id), p);
			}
		}

		return map;
	}

	private parseSemicolon(value: string): string[] {
		const str = (value ?? '').trim();
		if (!str) return [];
		return str.split(';');
	}

	// -------------- PRIVATE: SUPERFSECT BUILD --------------

	private buildSuperfSect(idsParcelasSet: Set<string>, showSectorMultiple: boolean): void {
		const sectores: SectorModel[] = getDropdownSelectedArray(this.lists.sectores);

		if (sectores.length === 0) {
			this.superfSect = [];
			return;
		}

		if (showSectorMultiple) {
			this.buildSuperfSectBySectorFromParcelasFiltered(idsParcelasSet, sectores);
			return;
		}

		this.superfSect = new Array<number>(sectores.length);

		for (let i = 0; i < sectores.length; i++) {
			const sector = sectores[i];

			const area = this.toNum(sector?.superficie_cultivada ?? sector?.superficie_sigpac ?? 0);

			this.superfSect[i] = Number(area.toFixed(2));
		}
	}

	private buildSuperfSectBySectorFromParcelasFiltered(idsParcelasSet: Set<string>, sectores: SectorModel[]): void {
		const sectorIndexById = new Map<unknown, number>();
		this.superfSect = new Array<number>(sectores.length).fill(0);

		for (let i = 0; i < sectores.length; i++) {
			const sector = sectores[i];
			if (!sector) continue;

			sectorIndexById.set(sector.id, i);

			if ((sector as any).proporcionArboles === 0) {
				(sector as any).proporcionArboles = 1;
			}
		}

		const filtered = (this.lists.parcelas.filtered ?? []) as Array<{ value: ParcelasModel }>;

		for (let i = 0; i < filtered.length; i++) {
			const parcela = filtered[i]?.value;
			if (!parcela) continue;

			const parcelaIdStr = String(parcela.id ?? '');
			if (!idsParcelasSet.has(parcelaIdStr)) continue;

			const idx = sectorIndexById.get(parcela.id_sector);
			if (idx === undefined) continue;

			const area = this.toNum(parcela.superficie_cultivada || parcela.superficie_sigpac || '0.00');

			if (this.superfSect[idx] !== undefined) {
				this.superfSect[idx] += area;
			}
		}

		for (let i = 0; i < this.superfSect.length; i++) {
			this.superfSect[i] = Number(this.superfSect[i]?.toFixed(2));
		}
	}

	// -------------- PRIVATE: TABLE VMS --------------

	private resetTotalesTabla(): void {
		this.totalesTabla.superficie = 0;
		this.totalesTabla.n = 0;
		this.totalesTabla.p = 0;
		this.totalesTabla.k = 0;
		this.totalesTabla.ca = 0;
		this.totalesTabla.s = 0;
		this.totalesTabla.mg = 0;
	}

	private buildNpkVms(): void {
		const showCa = !!this.env.showFertilizantesCa;
		const showS = !!this.env.showFertilizantesS;
		const showMg = !!this.env.showFertilizantesMg;

		const sectores: SectorModel[] = this.lists?.sectores ? getDropdownSelectedArray(this.lists.sectores) : [];

		this.sectorRowsVm = sectores.map((sector: SectorModel, i: number) => {
			const superf = this.superfSect?.[i] ?? 0;
			const hasSurface = this.isValidArea(this.toNum(superf));

			const row: NpkRowVm = {
				name: sector.nombre ?? '',
				superficieLabel: hasSurface ? this.fmt2(superf) : 'Sector sin superficie',
				hasSurface,
				n: hasSurface ? this.fmt2(this.udFertN?.[i]) : '-',
				p: hasSurface ? this.fmt2(this.udFertP?.[i]) : '-',
				k: hasSurface ? this.fmt2(this.udFertK?.[i]) : '-',
			};

			if (showCa) {
				row.ca = hasSurface ? this.fmt2(this.udFertCa?.[i]) : '-';
			}

			if (showS) {
				row.s = hasSurface ? this.fmt2(this.udFertS?.[i]) : '-';
			}

			if (showMg) {
				row.mg = hasSurface ? this.fmt2(this.udFertMg?.[i]) : '-';
			}

			return row;
		});

		const totalSuperficie = this.totalesTabla.superficie;

		this.totalsVm = {
			superficie: this.fmt2(this.totalesTabla.superficie),
			n: this.fmt2(this.totalesTabla.n),
			p: this.fmt2(this.totalesTabla.p),
			k: this.fmt2(this.totalesTabla.k),
			ca: showCa ? this.fmt2(this.totalesTabla.ca) : '',
			s: showS ? this.fmt2(this.totalesTabla.s) : '',
			mg: showMg ? this.fmt2(this.totalesTabla.mg) : '',
		};

		this.parcelaTotalsVm = {
			superficie: this.fmt2(totalSuperficie),
			n: this.fmt2(totalSuperficie > 0 ? this.totalesTabla.n / totalSuperficie : 0),
			p: this.fmt2(totalSuperficie > 0 ? this.totalesTabla.p / totalSuperficie : 0),
			k: this.fmt2(totalSuperficie > 0 ? this.totalesTabla.k / totalSuperficie : 0),
			ca: showCa ? this.fmt2(totalSuperficie > 0 ? this.totalesTabla.ca / totalSuperficie : 0) : '',
			s: showS ? this.fmt2(totalSuperficie > 0 ? this.totalesTabla.s / totalSuperficie : 0) : '',
			mg: showMg ? this.fmt2(totalSuperficie > 0 ? this.totalesTabla.mg / totalSuperficie : 0) : '',
		};

		const parcelasSelected: ParcelasModel[] = this.lists?.parcelas ? getDropdownSelectedArray(this.lists.parcelas) : [];

		this.parcelaRowsVm = parcelasSelected.map((parcela: ParcelasModel, i: number) => {
			const superfRaw = parcela.superficie_cultivada ?? parcela.superficie_sigpac ?? parcela.superficie ?? '0';

			const area = this.toNum(superfRaw);
			const hasSurface = this.isValidArea(area);

			const row: NpkRowVm = {
				name: parcela.nombre ?? '',
				superficieLabel: this.fmtSurfaceParcel(area),
				hasSurface,
				n: hasSurface ? this.fmt2(this.udFertN?.[i]) : '-',
				p: hasSurface ? this.fmt2(this.udFertP?.[i]) : '-',
				k: hasSurface ? this.fmt2(this.udFertK?.[i]) : '-',
			};

			if (showCa) {
				row.ca = hasSurface ? this.fmt2(this.udFertCa?.[i]) : '-';
			}

			if (showS) {
				row.s = hasSurface ? this.fmt2(this.udFertS?.[i]) : '-';
			}

			if (showMg) {
				row.mg = hasSurface ? this.fmt2(this.udFertMg?.[i]) : '-';
			}

			return row;
		});
	}

	// -------------- PRIVATE: HELPERS --------------

	private buildSelectedParcelasSet(value: unknown): Set<string> {
		const out = new Set<string>();

		const addId = (v: unknown) => {
			if (v == null) return;

			if (typeof v === 'object') {
				const id = (v as Record<string, unknown>)['id'];
				if (id != null) out.add(String(id));
				return;
			}

			const s = String(v).trim();
			if (!s) return;

			for (const part of s.split(/[;,]/)) {
				const p = part.trim();
				if (p) out.add(p);
			}
		};

		if (Array.isArray(value)) {
			for (const item of value) addId(item);
			return out;
		}

		addId(value);
		return out;
	}

	private toNum(value: unknown): number {
		if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

		const str = String(value ?? '').trim();
		if (!str) return 0;

		const n = Number(str.replace(',', '.'));
		return Number.isFinite(n) ? n : 0;
	}

	private isValidArea(area: number): boolean {
		return Number.isFinite(area) && area > 0;
	}

	private fmt2(value: unknown): string {
		return Utils.decimalFormat(this.toNum(value), 2) || this.zeroLabel;
	}

	private fmtSurfaceParcel(value: number): string {
		return Utils.decimalFormat(value, 2, '.', ',', 3) || this.zeroLabel;
	}
}
